/**
 * A host's map view (lib/map/hostView.ts) and the Mapbox map's camera, both ways, as plain maths: reading the view off a
 * host's Mapbox map, and the camera a follower's Mapbox map takes for it, eased there frame by frame
 * (docs/features/follow-host-map.md). Mapbox draws 512-pixel tiles, so at zoom z a pixel at latitude φ spans
 * 40,075,016.686 × cos φ ÷ (512 × 2^z) metres, and its camera stands ½H ÷ tan(½ fov) pixels from the centre of the view:
 * the frame's height there is the map's height in pixels times that.
 */

import { followDistance, type HostView, type HostViewPose, type HostViewStore } from './hostView'

/** The Earth's circumference at the equator, metres, as Mapbox measures it. */
const EARTH_CIRCUMFERENCE = 40_075_016.686
const TILE_SIZE = 512
const DEG = Math.PI / 180

/** Mapbox's vertical field of view, degrees, unless its transform says otherwise. */
export const MAPBOX_FOV = 36.87
/** Mapbox's steepest pitch, degrees from straight down. */
export const MAPBOX_MAX_PITCH = 85
/** How far behind the host's view a follower's map trails as it eases there, seconds: as the world's `HostViewController`. */
export const FOLLOW_EASE_SECONDS = 0.15

/** A Mapbox map's camera: its centre, zoom, bearing (degrees clockwise from north) and pitch (degrees from straight down). */
export interface MapboxCamera {
  latitude: number
  longitude: number
  zoom: number
  bearing: number
  pitch: number
}

/** The map's size in pixels. */
export interface MapSize {
  width: number
  height: number
}

/** Metres a pixel spans at a latitude and zoom. */
export function metresPerPixel(latitude: number, zoom: number): number {
  return (EARTH_CIRCUMFERENCE * Math.cos(latitude * DEG)) / (TILE_SIZE * 2 ** zoom)
}

const turned = (degrees: number) => ((degrees % 360) + 360) % 360
/** The short way from one angle to another, degrees in [−180, 180). */
const shortWay = (from: number, to: number) => turned(to - from + 180) - 180

/** The view a host's Mapbox map shows: its centre (on the ground: lift 0), its height in metres there, and which way it looks. Null for a map with no size. */
export function poseOfMapbox(camera: MapboxCamera, { width, height }: MapSize): HostViewPose | null {
  if (!(width > 0 && height > 0)) return null
  return {
    latitude: camera.latitude,
    longitude: camera.longitude,
    lift: 0,
    span: height * metresPerPixel(camera.latitude, camera.zoom),
    aspect: width / height,
    bearing: turned(camera.bearing),
    pitch: camera.pitch,
  }
}

/** A point in Mapbox's mercator space: x east and y south, each 0–1 across the world, and z up in the same units. */
export interface MercatorPoint {
  x: number
  y: number
  z: number
}

/** Where a place and height (metres) lie in mercator space. */
export function mercatorOf(latitude: number, longitude: number, altitude = 0): MercatorPoint {
  const y = (180 - (180 / Math.PI) * Math.log(Math.tan(Math.PI / 4 + (latitude * DEG) / 2))) / 360
  return { x: (longitude + 180) / 360, y, z: altitude / (EARTH_CIRCUMFERENCE * Math.cos(latitude * DEG)) }
}

/**
 * The view through the middle of a host's Mapbox map, from its camera and the ground under the middle (`point`, with its
 * height): how far the camera is from that point, which way it looks there, and the frame's height at that distance
 * (2 × distance × tan(½ fov)). A fit's padding stays on the map and moves its own centre off the canvas middle, so the
 * map's centre and zoom aren't always what its frame shows. Null without a distance, or looking at the sky.
 */
export function poseOfMapboxRay(
  { camera, point, fov }: { camera: MercatorPoint; point: { latitude: number; longitude: number; altitude: number }; fov: number },
  { width, height }: MapSize,
): HostViewPose | null {
  if (!(width > 0 && height > 0)) return null
  const ground = mercatorOf(point.latitude, point.longitude, point.altitude)
  const metre = 1 / (EARTH_CIRCUMFERENCE * Math.cos(point.latitude * DEG))
  const east = (ground.x - camera.x) / metre
  const north = (camera.y - ground.y) / metre
  const up = (ground.z - camera.z) / metre
  const distance = Math.hypot(east, north, up)
  if (!(distance > 0) || up >= 0) return null
  return {
    latitude: point.latitude,
    longitude: point.longitude,
    lift: 0,
    span: 2 * distance * Math.tan((fov * DEG) / 2),
    aspect: width / height,
    bearing: Math.hypot(east, north) < 1e-9 ? 0 : turned(Math.atan2(east, north) / DEG),
    pitch: Math.acos(-up / distance) / DEG,
  }
}

/**
 * The camera a follower's Mapbox map takes for a host's view: centred where the host's is, standing back as far as this
 * map needs (`followDistance`, the same rule as the world's), turned and tilted as the host's, no steeper than Mapbox
 * allows. The zoom puts that distance at ½H ÷ tan(½ fov) pixels: log2(40,075,016.686 × cos φ × ½H ÷ (512 × distance ×
 * tan(½ fov))).
 */
export function mapboxCameraOf(view: HostViewPose, map: MapSize & { fov: number; maxPitch: number }): MapboxCamera {
  const distance = followDistance(view, map.fov, map.width / map.height)
  const zoom = Math.log2((EARTH_CIRCUMFERENCE * Math.cos(view.latitude * DEG) * (map.height / 2)) / (TILE_SIZE * distance * Math.tan((map.fov * DEG) / 2)))
  return { latitude: view.latitude, longitude: view.longitude, zoom, bearing: view.bearing, pitch: Math.min(view.pitch, map.maxPitch) }
}

/**
 * One frame's step towards `to`, `delta` seconds on: the centre, the zoom (the distance in log space), the bearing and the
 * longitude the short way round, and the pitch, each a share 1 − e^(−delta ÷ 0.15 s) of the way, so the map trails the
 * host's view by about 0.15 s whatever the frame rate.
 */
export function easeMapboxCamera(from: MapboxCamera, to: MapboxCamera, delta: number): MapboxCamera {
  const share = 1 - Math.exp(-Math.max(0, delta) / FOLLOW_EASE_SECONDS)
  return {
    latitude: from.latitude + (to.latitude - from.latitude) * share,
    longitude: turned(from.longitude + shortWay(from.longitude, to.longitude) * share + 180) - 180,
    zoom: from.zoom + (to.zoom - from.zoom) * share,
    bearing: turned(from.bearing + shortWay(from.bearing, to.bearing) * share),
    pitch: from.pitch + (to.pitch - from.pitch) * share,
  }
}

/** Whether two cameras differ by more than anyone would see: about a centimetre, a ten-thousandth of a zoom level, a thousandth of a degree. */
export function mapboxCameraMoved(a: MapboxCamera, b: MapboxCamera): boolean {
  return (
    Math.abs(a.latitude - b.latitude) > 1e-7 ||
    Math.abs(shortWay(a.longitude, b.longitude)) > 1e-7 ||
    Math.abs(a.zoom - b.zoom) > 1e-4 ||
    Math.abs(shortWay(a.bearing, b.bearing)) > 1e-3 ||
    Math.abs(a.pitch - b.pitch) > 1e-3
  )
}

const NO_PADDING = { top: 0, right: 0, bottom: 0, left: 0 }

/** What following needs of a Mapbox map (mapbox-gl's `Map`). */
export interface FollowedMapboxMap {
  getCenter(): { lat: number; lng: number }
  getZoom(): number
  getBearing(): number
  getPitch(): number
  getMaxPitch(): number
  getContainer(): { clientWidth: number; clientHeight: number }
  jumpTo(options: { center: [number, number]; zoom: number; bearing: number; pitch: number; padding: typeof NO_PADDING }): unknown
  /** Mapbox's events: following hears `resize`, so it reads the map's size only when it changes. */
  on?(type: 'resize', listener: () => void): unknown
  off?(type: 'resize', listener: () => void): unknown
}

/** What reading a host's view needs of a Mapbox map, besides its camera. */
export interface ReadMapboxMap extends Pick<FollowedMapboxMap, 'getCenter' | 'getZoom' | 'getBearing' | 'getPitch' | 'getContainer'> {
  unproject(point: [number, number]): { lat: number; lng: number }
  getFreeCameraOptions(): { position?: MercatorPoint | null }
  queryTerrainElevation(lngLat: [number, number]): number | null | undefined
}

/** The map's camera now. */
export function cameraOfMapbox(map: Pick<FollowedMapboxMap, 'getCenter' | 'getZoom' | 'getBearing' | 'getPitch'>): MapboxCamera {
  const { lat, lng } = map.getCenter()
  return { latitude: lat, longitude: lng, zoom: map.getZoom(), bearing: turned(map.getBearing()), pitch: map.getPitch() }
}

/** Mapbox's field of view: its transform's (degrees), else the default. */
function fovOf(map: object): number {
  const fov = (map as { transform?: { fov?: unknown } }).transform?.fov
  return typeof fov === 'number' && fov > 0 ? fov : MAPBOX_FOV
}

/**
 * A host's view off their Mapbox map: through the middle of the canvas, from the camera to the ground there (on its
 * terrain, as drawn), whatever padding a fit left on the map (`poseOfMapboxRay`); from the map's centre and zoom when it
 * can't say where its camera is.
 */
export function readMapboxView(map: ReadMapboxMap): HostViewPose | null {
  const { clientWidth: width, clientHeight: height } = map.getContainer()
  if (!width || !height) return null
  const camera = map.getFreeCameraOptions().position
  if (camera) {
    const { lat, lng } = map.unproject([width / 2, height / 2])
    const pose = poseOfMapboxRay({ camera, point: { latitude: lat, longitude: lng, altitude: map.queryTerrainElevation([lng, lat]) ?? 0 }, fov: fovOf(map) }, { width, height })
    if (pose) return pose
  }
  return poseOfMapbox(cameraOfMapbox(map), { width, height })
}

/**
 * Follows a host's view on a Mapbox map (docs/features/follow-host-map.md): every frame it eases the map's camera towards
 * the newest view in `store`, from where the map was, and applies it with `jumpTo` (a move by code, which the map's own
 * move handlers never take for the viewer's), without padding, so the host's centre is the middle of the canvas. Until a
 * view arrives, or while the map has no size, the map stays where it is. Under reduced motion it doesn't glide: it cuts
 * to each view the host settled on. Returns the stop.
 *
 * Cheap every frame: the camera a view asks for is worked out once per view, the map's size is read again only after
 * Mapbox says it resized (reading it every frame can force the page's layout), and once the map has arrived a frame
 * does nothing at all until the next view.
 */
export function followOnMapbox(map: FollowedMapboxMap, store: HostViewStore, { reducedMotion = false } = {}): () => void {
  let frame = 0
  let last: number | null = null
  // Where the ease has got to, and what the map was last given (the first view always goes, taking the padding off).
  let eased: MapboxCamera | null = null
  let applied: MapboxCamera | null = null
  // The camera the newest view asks for on this map, and the view it was worked out for.
  let goal: MapboxCamera | null = null
  let goalOf: HostView | null = null
  let size: MapSize | null = null
  const resized = () => {
    size = null
  }
  map.on?.('resize', resized)
  const step = (now: number) => {
    frame = requestAnimationFrame(step)
    const delta = last === null ? 0 : (now - last) / 1000
    last = now
    const view = reducedMotion ? store.settled : store.latest
    if (!view) return
    if (!size) {
      const { clientWidth: width, clientHeight: height } = map.getContainer()
      if (!width || !height) return
      size = { width, height }
      goalOf = null
    }
    if (view !== goalOf) {
      const next = mapboxCameraOf(view, { ...size, fov: fovOf(map), maxPitch: Math.min(MAPBOX_MAX_PITCH, map.getMaxPitch()) })
      if (!Number.isFinite(next.zoom)) return
      goal = next
      goalOf = view
    } else if (eased === goal && applied === goal) {
      return
    }
    if (!goal) return
    const next = reducedMotion ? goal : easeMapboxCamera(eased ?? cameraOfMapbox(map), goal, delta)
    // Closer than anyone could see is there.
    eased = mapboxCameraMoved(next, goal) ? next : goal
    if (applied && !mapboxCameraMoved(eased, applied)) {
      applied = eased
      return
    }
    applied = eased
    map.jumpTo({ center: [eased.longitude, eased.latitude], zoom: eased.zoom, bearing: eased.bearing, pitch: eased.pitch, padding: NO_PADDING })
  }
  frame = requestAnimationFrame(step)
  return () => {
    cancelAnimationFrame(frame)
    map.off?.('resize', resized)
  }
}
